import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { HEATMAPROOTWIDTH, illuminanceScale } from "consts";
import { roundToTwo } from "utils/helpers";
import { ISdFpath } from "types";
import { Illuminance, SidewalkStateItem, SurfaceWithIlluminance } from "../../types";
import { useIlluminanceData } from "../../hooks/useIlluminanceData";
import Heatmap from "./Heatmap";
import getStyles from "./styles";

type Props = {
  id: string;
  illuminance: Illuminance;
  surfaces: SurfaceWithIlluminance[];
  sidewalkPlacing: string | null;
  subVal: ISdFpath[] | undefined;
  subValResult: SidewalkStateItem[];
};

const IlluminanceBordersHeatmap: FC<Props> = ({ id, illuminance, surfaces, sidewalkPlacing, subVal, subValResult }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).LuminanceHeatmap, [theme]);

  const {
    ptsX_left,
    ptsY_left,
    dimX_left,
    dimY_left,
    ptsX_right,
    ptsY_right,
    dimX_right,
    dimY_right,
    illuminanceData_left,
    illuminanceData_right,
    maxIlluminanceValue,
    maxIlluminanceValue_left,
    maxIlluminanceValue_right,
    heightLeftHeatmap,
    heightRightHeatmap,
  } = useIlluminanceData({ illuminance, surfaces, sidewalk_placing: sidewalkPlacing, subVal, subValResult });

  return (
    <Box component="section" sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Illuminance calculation field (heatmap diagram):")} <span id="dimX1">{dimX_left}</span>m &times; <span id="dimY1">{roundToTwo(dimY_left)}</span>m (
        <span id="ptsX1">{ptsX_left}</span> &times; <span id="ptsY1">{ptsY_right}</span> {t("pts")})
      </Typography>

      <Stack component={Paper} sx={styles.card}>
        {illuminanceData_left.length > 0 && (
          <Heatmap
            id={id ? `${id}-heatmaproot-left-border` : "heatmaproot-left-border"}
            width={HEATMAPROOTWIDTH}
            data={illuminanceData_left}
            ptsX={ptsX_left}
            ptsY={ptsY_left}
            dimX={dimX_left}
            dimY={dimY_left}
            maxValue={maxIlluminanceValue}
            maxValue_left={maxIlluminanceValue_left}
            maxValue_right={maxIlluminanceValue_right}
            updatedHeight={heightLeftHeatmap}
            disableXAxis={true}
            scale={illuminanceScale}
            legendTitle={t("Illuminance (lux)")}
            heatmapTitle={t("Border Area Left")}
          />
        )}

        {illuminanceData_right.length > 0 && (
          <Heatmap
            id={id ? `${id}-heatmaproot-right-border` : "heatmaproot-right-border"}
            width={HEATMAPROOTWIDTH}
            data={illuminanceData_right}
            ptsX={ptsX_right}
            ptsY={ptsY_right}
            dimX={dimX_right}
            dimY={dimY_right}
            maxValue={maxIlluminanceValue}
            maxValue_left={maxIlluminanceValue_left}
            maxValue_right={maxIlluminanceValue_right}
            updatedHeight={heightRightHeatmap}
            disableXAxis={true}
            scale={illuminanceScale}
            legendTitle={t("Illuminance (lux)")}
            heatmapTitle={t("Border Area Right")}
          />
        )}
      </Stack>
    </Box>
  );
};

export default IlluminanceBordersHeatmap;
